import React, { useCallback } from 'react';
import { FolderItem } from './organization/FolderStructure';
import { TopNavigationBar } from './TopNavigationBar';
import AppSidebarContent from './AppSidebarContent';
import { useSidebar } from '../sidebar';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useFolderStructureVisibility, FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';
import { Folder } from '@/lib/stores/folderStore';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

// Import extracted components
import { BorderEffect, BorderEffectType } from './components/BorderEffect';
import { FolderStructureWithManagement } from './components/FolderStructureWithManagement';

// Import extracted hooks
import { useFolderTypeManagement } from './hooks/useFolderTypeManagement';
import { useUnifiedSidebarHandlers } from './hooks/useUnifiedSidebarHandlers';
import { useItemMoveToFolder } from './hooks/useItemMoveToFolder';

// Import data contexts for item counts
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useDocumentDataContext } from '@/providers/DocumentDataProvider';
import { useDialogDataContext } from '@/providers/DialogDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';

interface UnifiedSidebarProps {
  borderEffect?: BorderEffectType;
}

export default function UnifiedSidebar({ 
  borderEffect = 'gradient'
}: UnifiedSidebarProps) {
  const { t } = usePlatformTranslation();
  const { open: sidebarOpen } = useSidebar();
  const { selectedGame, selectedCompany } = useSimplifiedUserData();
  
  // Folder structure visibility context
  const { 
    selectedFolders,
    setSelectedFolderForType,
    toggleExpanded,
    isTypeExpanded,
    isFolderStructureVisible,
  } = useFolderStructureVisibility();
  
  // Get data contexts for item counts
  const { glossaryItems } = useGlossaryDataContext();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { documents } = useDocumentDataContext();
  const { dialogs } = useDialogDataContext();
  const { types } = useTypesDataContext();
  
  // Custom hooks for folder type management
  const { availableFolderTypes, showFolderStructure } = useFolderTypeManagement({
    selectedGame,
    selectedCompany,
  });
  
  // Custom hooks for item handlers
  const handlers = useUnifiedSidebarHandlers({ selectedGame });
  
  // Custom hook for global move to folder events
  useItemMoveToFolder({ selectedGame });


  // Calculate total width for the unified sidebar
  const calculateTotalWidth = (): string => {
    let width = 48;  // 3rem - main sidebar always visible (icon-only)
    
    // Add folder structure width if visible
    if (showFolderStructure && isFolderStructureVisible) {
      width += 256; // 16rem folder structure
    }
    
    return `${width}px`;
  };

  // Recursively convert folders to FolderItem format
  const convertFoldersToFolderItems = useCallback((folders: Folder[], parentId?: string): FolderItem[] => {
    return folders.map(folder => ({
      id: folder.id,
      name: folder.name,
      parentId: parentId,
      children: folder.children ? convertFoldersToFolderItems(folder.children, folder.id) : undefined,
      itemCount: folder.itemCount
    }));
  }, []);

  // Get item count for a folder type
  const getItemCountForType = useCallback((folderType: FolderStructureType): number => {
    switch (folderType) {
      case 'library': return glossaryItems?.length || 0;
      case 'documents': return documents?.length || 0;
      case 'blueprints': return glossaryBlueprints?.length || 0;
      case 'dialogs': return dialogs?.length || 0;
      case 'variables': return types?.length || 0;
      case 'flows': {
        // Note: Flow count is handled dynamically via the handlers hook
        // This is a simplified count for display purposes
        return 0;
      }
      default: return 0;
    }
  }, [glossaryItems, documents, glossaryBlueprints, dialogs, types]);

  return (
    <>
      {/* Unified Sidebar Container */}
      <div 
        className="relative flex flex-col h-full transition-all duration-200 ease-in-out"
        style={{ width: calculateTotalWidth() }}
      >
        {/* Top Navigation Bar */}
        <div className="h-12 relative">
          <TopNavigationBar 
            width={calculateTotalWidth()} 
            sidebarOpen={false}
            hasFolderStructure={showFolderStructure && isFolderStructureVisible}
          />
        </div>
        
        {/* Sidebar Content Container - positioned below top nav */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main App Sidebar Container */}
          <div className="relative">
            <AppSidebarContent />
          </div>
          
          {/* Folder Structures Container */}
          {showFolderStructure && isFolderStructureVisible && (
            <div className="relative flex flex-col h-full w-64 bg-sidebar overflow-y-auto">
              {/* Multiple Folder Structures - stacked vertically - Show ALL available types */}
              {availableFolderTypes.map((folderType) => (
                <FolderStructureWithManagement
                  key={folderType}
                  folderType={folderType}
                  selectedGame={selectedGame}
                  selectedFolders={selectedFolders}
                  isTypeExpanded={isTypeExpanded}
                  toggleExpanded={toggleExpanded}
                  setSelectedFolderForType={setSelectedFolderForType}
                  getItemCountForType={getItemCountForType}
                  handleItemClick={handlers.handleItemClick}
                  handleItemRename={handlers.handleItemRename}
                  handleItemDelete={handlers.handleItemDelete}
                  handleItemDuplicate={handlers.handleItemDuplicate}
                  handlecreateObject={handlers.handlecreateObject}
                  handleCreateDocument={handlers.handleCreateDocument}
                  handleCreateBlueprint={handlers.handleCreateBlueprint}
                  handleCreateDialog={handlers.handleCreateDialog}
                  handleCreateFlow={handlers.handleCreateFlow}
                  handleCreateVariable={handlers.handleCreateVariable}
                  convertFoldersToFolderItems={convertFoldersToFolderItems}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Border Effect - Full Height, always on the end edge (right in LTR, left in RTL) */}
        <div className="absolute inset-y-0 end-0 z-50 pointer-events-none">
          <BorderEffect type={borderEffect} />
        </div>
      </div>
    </>
  );
}
